import { useRef, useState, type DragEvent } from "react";

import styles from "./UploadStep.module.css";

import { ImportIcon } from "@/components/icons";

interface UploadStepProps {
  file: File | null;
  error: string | null;
  isWorking: boolean;
  statusError: string | null;
  onSelectFile: (file: File) => void;
  onTransform: () => void;
}

export function UploadStep({
  file,
  error,
  isWorking,
  statusError,
  onSelectFile,
  onTransform,
}: UploadStepProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function handleDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setDragging(false);
    const dropped = event.dataTransfer.files[0];
    if (dropped) onSelectFile(dropped);
  }

  return (
    <section className={styles.step} aria-labelledby="upload-heading">
      <h2 id="upload-heading" className={styles.heading}>
        Upload contacts export
      </h2>
      <p className={styles.intro}>
        Upload your NetSuite contacts Excel export. The tool will transform it into two
        ready-to-import CSVs — one for email, one for SMS.
      </p>

      <button
        type="button"
        className={dragging ? `${styles.dropzone} ${styles.dragging}` : styles.dropzone}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        aria-describedby="upload-hint"
      >
        <ImportIcon className={styles.dropIcon} aria-hidden="true" />
        <span className={styles.dropTitle}>Drag &amp; drop your Excel file here</span>
        <span id="upload-hint" className={styles.dropHint}>
          or click to browse — max 10 MB
        </span>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          className={styles.fileInput}
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            if (chosen) onSelectFile(chosen);
            event.target.value = "";
          }}
        />
      </button>

      {file ? (
        <p className={styles.fileName}>
          Selected: <strong>{file.name}</strong>
        </p>
      ) : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      {statusError ? (
        <p className={styles.error} role="alert">
          {statusError}
        </p>
      ) : null}

      <button
        type="button"
        className={styles.primary}
        disabled={!file || isWorking}
        onClick={onTransform}
      >
        {isWorking ? "Transforming…" : "Transform contacts"}
      </button>
    </section>
  );
}
